import React, { useState, useMemo } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import "./order.css";
import SingleDrmtlgyOrderStatus from "./singleDrmtlgyOrderStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const DrmtlgyOrderBulkStatus = () => {
    const [file, setFile] = useState(null);
    const [currentOrders, setCurrentOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(25);

    // CSV Export function
    const exportToCSV = () => {
        if (currentOrders.length === 0) {
            alert("No data to export");
            return;
        }

        const headers = [
            "Order ID",
            "Store Order No",
            "Customer Name",
            "City",
            "State",
            "Zipcode",
            "Country",
            "Created Date",
            "Processed Date",
            "Order Item Name",
            "Order Item Qty",
            "Refunded Item Name",
            "Refunded Item Qty",
            "Fulfillable Qty",
            "Fulfilled Qty",
            "Item Fulfillment Status",
            "Total",
            "Status",
            "Fulfillment Status",
            "Fulfillment Method",
            "Shipment Status",
            "Shipper Name",
            "Tracking Number"
        ];

        const rows = [];

        currentOrders.forEach((order) => {
            if (order.error) return; // Skip orders with errors

            const address =
                order.shipping_address ||
                order.billing_address ||
                order.customer?.default_address ||
                {};

            // Build refunded quantity map for this order
            const refundedMap = {};
            order.refunds?.forEach((refund) => {
                refund.refund_line_items?.forEach((rli) => {
                    if (rli.line_item_id) {
                        refundedMap[rli.line_item_id] =
                            (refundedMap[rli.line_item_id] || 0) + (rli.quantity || 0);
                    }
                });
            });

            // Normal order items
            order.line_items?.forEach((item) => {
                const rawFulfilled = (item.quantity || 0) - (item.fulfillable_quantity || 0);
                const refundedQty = refundedMap[item.id] || 0;
                const fulfilledQty = Math.max(0, rawFulfilled - refundedQty);

                // Function to get line item fulfillment status
                const getLineItemFulfillmentStatus = () => {
                    if (fulfilledQty > 0) return "fulfilled";
                    if (item.fulfillment_status) return item.fulfillment_status;
                    if (order.fulfillments?.length) {
                        for (const fulfillment of order.fulfillments) {
                            for (const fItem of fulfillment.line_items || []) {
                                if (fItem.id === item.id) {
                                    return fulfillment.shipment_status || "fulfilled";
                                }
                            }
                        }
                    }
                    return "unfulfilled";
                };

                rows.push([
                    order.id,
                    order.name || "—",
                    `${order.customer?.first_name || ""} ${order.customer?.last_name || ""}`.trim(),
                    address.city || "—",
                    address.province || address.province_code || "—",
                    address.zip || "—",
                    address.country || address.country_code || "—",
                    order.created_at ? new Date(order.created_at).toLocaleDateString() : "—",
                    order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—",
                    item.name,
                    item.quantity,
                    "—",
                    refundedQty,
                    item.fulfillable_quantity || 0,
                    fulfilledQty,
                    getLineItemFulfillmentStatus(),
                    order.total_price || "—",
                    order.financial_status || "—",
                    order.fulfillment_status || "unfulfilled",
                    item.fulfillment_service || "—",
                    order.fulfillments?.[0]?.shipment_status || "—",
                    order.fulfillments?.[0]?.tracking_company || "—",
                    order.fulfillments?.[0]?.tracking_number || "—",
                ]);
            });

            // Refunded items
            order.refunds?.forEach((refund) => {
                refund.refund_line_items?.forEach((rli) => {
                    const refundedItem =
                        rli.line_item ||
                        order.line_items?.find((li) => li.id === rli.line_item_id);

                    rows.push([
                        order.id,
                        order.name || "—",
                        `${order.customer?.first_name || ""} ${order.customer?.last_name || ""}`.trim(),
                        address.city || "—",
                        address.province || address.province_code || "—",
                        address.zip || "—",
                        address.country || address.country_code || "—",
                        order.created_at ? new Date(order.created_at).toLocaleDateString() : "—",
                        order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—",
                        "—",
                        "—",
                        refundedItem?.name || "—",
                        rli.quantity || 0,
                        "—",
                        "—",
                        "refunded",
                        order.total_price || "—",
                        order.financial_status || "—",
                        order.fulfillment_status || "unfulfilled",
                        refundedItem?.fulfillment_service || "—",
                        order.fulfillments?.[0]?.shipment_status || "—",
                        order.fulfillments?.[0]?.tracking_company || "—",
                        order.fulfillments?.[0]?.tracking_number || "—",
                    ]);
                });
            });
        });

        const csvContent = [
            headers.join(","),
            ...rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")),
        ].join("\r\n");

        const blob = new Blob(["\ufeff", csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", `drmtlgy_orders_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    // Pagination logic
    const paginationData = useMemo(() => {
        const indexOfLastOrder = currentPage * ordersPerPage;
        const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
        const currentOrdersSlice = currentOrders.slice(indexOfFirstOrder, indexOfLastOrder);
        const totalPages = Math.ceil(currentOrders.length / ordersPerPage);

        return { currentOrdersSlice, totalPages };
    }, [currentOrders, currentPage, ordersPerPage]);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleClear = () => {
        setCurrentOrders([]);
        setError(null);
        setFile(null);
        setCurrentPage(1);
    };

    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            setFile(uploadedFile);
            setError(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setError("Please select a CSV file first");
            return;
        }

        setError(null);
        setLoading(true);
        setCurrentOrders([]);
        setCurrentPage(1);

        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const csvData = event.target.result;
                const lines = csvData.split('\n');
                const ordersArray = [];

                // Skip header and extract order numbers
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (line) {
                        // Try to find order number in different possible columns
                        const columns = line.split(',');
                        for (const col of columns) {
                            const trimmedCol = col.trim().replace(/"/g, '');
                            if (trimmedCol && !isNaN(trimmedCol) && trimmedCol.length > 3) {
                                ordersArray.push(trimmedCol);
                                break;
                            }
                        }
                    }
                }

                if (ordersArray.length === 0) {
                    setError("No valid order numbers found in the CSV file");
                    setLoading(false);
                    return;
                }

                await fetchOrders(ordersArray);
            };
            reader.readAsText(file);
        } catch (err) {
            setError("Error reading CSV file");
            setLoading(false);
        }
    };

    const fetchOrders = async (ordersArray) => {
        try {
            const results = [];
            for (const orderNumber of ordersArray) {
                try {
                    const response = await axios.get(
                        `${API_BASE_URL}/api/drmtlgy/shopify/order-status/${orderNumber}`,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
                    );
                    results.push(response.data.order);
                } catch (err) {
                    results.push({
                        orderNumber,
                        error: err.response?.data?.error || "Order not found or error fetching order",
                    });
                }
            }
            setCurrentOrders(results);
        } catch (err) {
            setError("Unexpected error occurred while fetching orders.");
        } finally {
            setLoading(false);
        }
    };

    // Pagination component
    const Pagination = () => {
        if (paginationData.totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(paginationData.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav>
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(1)}>
                            First
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                            Previous
                        </button>
                    </li>
                    {pageNumbers.map(number => (
                        <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => paginate(number)}>
                                {number}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                            Next
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(paginationData.totalPages)}>
                            Last
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4">DRMTLGY Bulk Order Status</h2>
            <div className="d-flex justify-content-end mb-4">
                <a href="/Order Template file.csv" download className="btn btn-primary">
                    Download Order Template
                </a>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="ordersFile" className="form-label">
                                Upload CSV File
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                id="ordersFile"
                                accept=".csv"
                                onChange={handleFileUpload}
                                required
                            />
                            <div className="form-text">
                                CSV should contain order numbers in any column. The first row is treated as header.
                                Maximum 100 orders per file.
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-warning" disabled={loading}>
                                {loading ? "Processing..." : "Check Orders"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClear}
                                disabled={loading}
                            >
                                Clear
                            </button>
                            <button
                                type="button"   // 👈 prevents form submission
                                onClick={exportToCSV}
                                className="btn btn-success"
                                disabled={currentOrders.length === 0 || loading}
                            >
                                Export to CSV
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {loading && (
                <div className="text-center">
                    <ClipLoader size={35} color={"#123abc"} loading={loading} />
                    <p>Loading orders...</p>
                </div>
            )}

            {error && <div className="alert alert-danger">{error}</div>}

            {!loading && currentOrders.length > 0 && (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            Showing {((currentPage - 1) * ordersPerPage) + 1} to {Math.min(currentPage * ordersPerPage, currentOrders.length)} of {currentOrders.length} orders
                        </div>
                        <div>
                            <strong>Page {currentPage} of {paginationData.totalPages}</strong>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th>ID</th>
                                    <th>Store Order No</th>
                                    <th>Customer Name</th>
                                    <th>City</th>
                                    <th>State</th>
                                    <th>Zipcode</th>
                                    <th>Country</th>
                                    <th>Created Date</th>
                                    <th>Processed Date</th>
                                    <th>Order Item Name</th>
                                    <th>Order Item Qty</th>
                                    <th>Refunded Item Name</th>
                                    <th>Refunded Item Qty</th>
                                    <th>Fulfillable Qty</th>
                                    <th>Fulfilled Qty</th>
                                    <th>Item Fulfillment Status</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Fulfillment Status</th>
                                    <th>Fulfillment Method</th>
                                    <th>Shipment Status</th>
                                    <th>Shipper Name</th>
                                    <th>Tracking Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginationData.currentOrdersSlice.length > 0 ? (
                                    paginationData.currentOrdersSlice.flatMap((order) => {
                                        if (order.error) {
                                            return (
                                                <tr key={order.orderNumber}>
                                                    <td colSpan="23" className="text-center text-danger">
                                                        Error for order {order.orderNumber}: {order.error}
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        const address =
                                            order.shipping_address ||
                                            order.billing_address ||
                                            order.customer?.default_address ||
                                            {};

                                        const rows = [];

                                        // 🔹 Build refunded qty map for this order
                                        const refundedMap = {};
                                        order.refunds?.forEach((refund) => {
                                            refund.refund_line_items?.forEach((rli) => {
                                                if (rli.line_item_id) {
                                                    refundedMap[rli.line_item_id] =
                                                        (refundedMap[rli.line_item_id] || 0) + (rli.quantity || 0);
                                                }
                                            });
                                        });

                                        // 🔹 Normal order items
                                        order.line_items?.forEach((item, idx) => {
                                            // Raw fulfilled qty
                                            const rawFulfilled =
                                                (item.quantity || 0) - (item.fulfillable_quantity || 0);

                                            // Refunded qty for this item
                                            const refundedQty = refundedMap[item.id] || 0;

                                            // ✅ Final fulfilled excludes refunded qty
                                            const fulfilledQty = Math.max(0, rawFulfilled - refundedQty);

                                            // Function to get line item fulfillment status
                                            const getLineItemFulfillmentStatus = () => {
                                                if (fulfilledQty > 0) return "fulfilled";

                                                if (item.fulfillment_status) {
                                                    return item.fulfillment_status;
                                                }

                                                if (order.fulfillments?.length) {
                                                    for (const fulfillment of order.fulfillments) {
                                                        for (const fItem of fulfillment.line_items || []) {
                                                            if (fItem.id === item.id) {
                                                                return fulfillment.shipment_status || "fulfilled";
                                                            }
                                                        }
                                                    }
                                                }
                                                return "unfulfilled";
                                            };

                                            rows.push(
                                                <tr key={`${order.id}-item-${idx}`}>
                                                    <td>{order.id}</td>
                                                    <td>{order.name || "—"}</td>
                                                    <td>{order.customer?.first_name || ""} {order.customer?.last_name || ""}</td>
                                                    <td>{address.city || "—"}</td>
                                                    <td>{address.province || address.province_code || "—"}</td>
                                                    <td>{address.zip || "—"}</td>
                                                    <td>{address.country || address.country_code || "—"}</td>
                                                    <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td>
                                                    <td>{order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—"}</td>

                                                    {/* Order item columns */}
                                                    <td>{item.name}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>—</td> {/* Refunded Item Name */}
                                                    <td>{refundedQty}</td> {/* ✅ Refunded Item Qty */}
                                                    <td>{item.fulfillable_quantity || 0}</td>
                                                    <td>{fulfilledQty}</td> {/* ✅ Fulfilled Qty */}
                                                    <td>{getLineItemFulfillmentStatus()}</td>

                                                    {/* Order-level columns */}
                                                    <td>{order.total_price || "—"}</td>
                                                    <td>{order.financial_status || "—"}</td>
                                                    <td
                                                        style={{
                                                            color: order.fulfillment_status === "fulfilled" ? "lightgreen" : "inherit",
                                                            fontWeight: order.fulfillment_status === "fulfilled" ? "bold" : "normal",
                                                        }}
                                                    >
                                                        {order.fulfillment_status || "unfulfilled"}
                                                    </td>
                                                    <td>{item.fulfillment_service || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.shipment_status || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.tracking_company || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.tracking_number || "—"}</td>
                                                </tr>
                                            );
                                        });

                                        // 🔹 Refunded items section (separate rows)
                                        order.refunds?.forEach((refund) => {
                                            refund.refund_line_items?.forEach((rli, idx) => {
                                                const refundedItem =
                                                    rli.line_item ||
                                                    order.line_items?.find((li) => li.id === rli.line_item_id);

                                                let refundedFulfillmentStatus =
                                                    refundedItem?.fulfillment_status || "unfulfilled";

                                                rows.push(
                                                    <tr key={`${order.id}-refund-${idx}`}>
                                                        <td>{order.id}</td>
                                                        <td>{order.name || "—"}</td>
                                                        <td>{order.customer?.first_name || ""} {order.customer?.last_name || ""}</td>
                                                        <td>{address.city || "—"}</td>
                                                        <td>{address.province || address.province_code || "—"}</td>
                                                        <td>{address.zip || "—"}</td>
                                                        <td>{address.country || address.country_code || "—"}</td>
                                                        <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td>
                                                        <td>{order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—"}</td>

                                                        {/* Refunded item columns */}
                                                        <td>—</td> {/* Order Item Name */}
                                                        <td>—</td> {/* Order Item Qty */}
                                                        <td>{refundedItem?.name || "—"}</td>
                                                        <td>{rli.quantity || 0}</td>
                                                        <td>—</td> {/* Fulfillable Qty */}
                                                        <td>—</td> {/* Fulfilled Qty */}
                                                        <td>{refundedFulfillmentStatus}</td>

                                                        {/* Order-level columns */}
                                                        <td>{order.total_price || "—"}</td>
                                                        <td>{order.financial_status || "—"}</td>
                                                        <td
                                                            style={{
                                                                color: order.fulfillment_status === "fulfilled" ? "lightgreen" : "inherit",
                                                                fontWeight: order.fulfillment_status === "fulfilled" ? "bold" : "normal",
                                                            }}
                                                        >
                                                            {order.fulfillment_status || "unfulfilled"}
                                                        </td>
                                                        <td>{refundedItem?.fulfillment_service || "—"}</td>
                                                        <td>{order.fulfillments?.[0]?.shipment_status || "—"}</td>
                                                        <td>{order.fulfillments?.[0]?.tracking_company || "—"}</td>
                                                        <td>{order.fulfillments?.[0]?.tracking_number || "—"}</td>
                                                    </tr>
                                                );
                                            });
                                        });

                                        return rows;
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="23" className="text-center">No orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination />
                </>
            )}
            <hr />
            <SingleDrmtlgyOrderStatus />
        </div>
    );
};

export default DrmtlgyOrderBulkStatus;